import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { TenantDetail } from "../api/types";
import { TENANT_STATUSES, removalReasonLabel, tenantStatusBadge, tenantStatusLabel } from "../lib/tenants";
import { formatDateTime } from "../lib/workorders";

export function TenantDetailPage() {
  const { id } = useParams();
  const tenantId = Number(id);

  const tenantQ = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => api.tenants.get(tenantId),
    enabled: Number.isInteger(tenantId),
  });

  if (tenantQ.isLoading) {
    return (
      <Layout title="Tenant">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (tenantQ.isError || !tenantQ.data) {
    return (
      <Layout title="Tenant">
        <section className="card">
          <p>That tenant could not be found.</p>
          <Link to="/tenants" className="btn btn-ghost btn-xl">
            Back to Tenants
          </Link>
        </section>
      </Layout>
    );
  }

  return <TenantDetailView tenant={tenantQ.data.tenant} />;
}

function TenantDetailView({ tenant }: { tenant: TenantDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const removed = tenant.status === "former";

  const [firstName, setFirstName] = useState(tenant.firstName);
  const [lastName, setLastName] = useState(tenant.lastName);
  const [dateOfBirth, setDateOfBirth] = useState(tenant.dateOfBirth ?? "");
  const [phone, setPhone] = useState(tenant.phone ?? "");
  const [email, setEmail] = useState(tenant.email ?? "");
  const [emergencyName, setEmergencyName] = useState(tenant.emergencyContactName ?? "");
  const [emergencyPhone, setEmergencyPhone] = useState(tenant.emergencyContactPhone ?? "");
  const [status, setStatus] = useState(tenant.status);
  const [unitId, setUnitId] = useState(tenant.unitId ? String(tenant.unitId) : "");
  const [moveInDate, setMoveInDate] = useState(tenant.moveInDate ?? "");
  const [notes, setNotes] = useState(tenant.notes ?? "");
  const [saved, setSaved] = useState(false);

  const unitsQ = useQuery({
    queryKey: ["ref", "units", tenant.buildingId],
    queryFn: () => api.reference.units(tenant.buildingId),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      api.tenants.update(tenant.id, {
        unitId: unitId ? Number(unitId) : null,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: dateOfBirth || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        emergencyContactName: emergencyName.trim() || null,
        emergencyContactPhone: emergencyPhone.trim() || null,
        status,
        moveInDate: moveInDate || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["tenant", tenant.id] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const leaseMut = useMutation({
    mutationFn: () => api.tenants.leaseAgreement(tenant.id),
    onSuccess: ({ pdfDataUrl, filename }) => {
      const a = document.createElement("a");
      a.href = pdfDataUrl;
      a.download = filename;
      a.click();
    },
  });

  return (
    <Layout title="Tenant Profile">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>
              {tenant.firstName} {tenant.lastName}
            </h2>
            <p className="sub">
              {tenant.where} · {tenant.buildingName}
            </p>
          </div>
          <span className={`badge badge-${tenantStatusBadge(tenant.status)}`}>
            {tenantStatusLabel(tenant.status)}
          </span>
        </div>

        {removed && (
          <p className="help-line">
            This tenant has been removed (<strong>{removalReasonLabel(tenant.removalReason)}</strong>
            {tenant.moveOutDate ? ` on ${tenant.moveOutDate}` : ""}). Their record is kept for
            history.
          </p>
        )}

        <div className="two-col">
          <label className="field">
            <span>First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </label>
          <label className="field">
            <span>Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span>Unit</span>
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">No unit assigned</option>
            {(unitsQ.data?.units ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                Unit {u.unitNumber}
                {u.floor ? `, floor ${u.floor}` : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Date of birth</span>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {TENANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {tenantStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
          <label className="field">
            <span>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Emergency contact name</span>
            <input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} />
          </label>
          <label className="field">
            <span>Emergency contact phone</span>
            <input
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
              inputMode="tel"
            />
          </label>
        </div>

        <label className="field">
          <span>Move-in date</span>
          <input type="date" value={moveInDate} onChange={(e) => setMoveInDate(e.target.value)} />
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          {!removed && (
            <Link to={`/tenants/${tenant.id}/remove`} className="btn btn-danger btn-xl">
              Remove tenant
            </Link>
          )}
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => leaseMut.mutate()}
            disabled={leaseMut.isPending}
          >
            {leaseMut.isPending ? "Generating..." : "Generate lease PDF"}
          </button>
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/tenants")}
            disabled={saveMut.isPending}
          >
            Back to list
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>History</h2>
        {tenant.history.length === 0 ? (
          <p className="sub">No work orders or incidents tied to this tenant/unit yet.</p>
        ) : (
          <ul className="timeline">
            {tenant.history.map((h) => (
              <li key={`${h.kind}-${h.id}`}>
                <strong>{formatDateTime(h.date)}</strong> - {h.kind === "work_order" ? "Work order" : "Incident"}:{" "}
                {h.title} ({h.status}) at {h.where}
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
}
